import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SchoolIcon from '@mui/icons-material/School';
import HandshakeIcon from '@mui/icons-material/Handshake';
import BusinessIcon from '@mui/icons-material/Business';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';

const roles = [
  {
    title: 'Player',
    description: 'Track skills, learn, and compare with peers.',
    icon: <PersonAddIcon />,
    color: '#1976d2',
    path: '/register/player',
  },
  {
    title: 'Coach',
    description: 'Manage students, rankings and academy growth.',
    icon: <SchoolIcon />,
    color: '#7c3aed',
    path: '/register/coach',
  },
  {
    title: 'Academy',
    description: 'Register your school, college or sports academy on the platform.',
    icon: <BusinessIcon />,
    color: '#0d9488',
    path: '/register/academy',
  },
  {
    title: 'Corporate',
    description: 'Register your company so employees can compete and appear on your public profile.',
    icon: <WorkOutlineIcon />,
    color: '#6116A7',
    path: '/register/academy?type=corporate',
  },
  {
    title: 'Sponsors & Organizers',
    description: 'Partner across sports for contests, tournaments and events.',
    icon: <HandshakeIcon />,
    color: '#ea580c',
    path: '/partners',
  },
];

const LandingConvertSection: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        mt: { xs: 5, md: 6 },
        p: { xs: 3.5, md: 5 },
        borderRadius: 4,
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #1d4ed8 100%)',
        color: '#fff',
        boxShadow: '0 24px 64px rgba(15, 23, 42, 0.25)',
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Sora", Arial, sans-serif',
          fontWeight: 800,
          mb: 1,
          textAlign: 'center',
          fontSize: { xs: '1.7rem', md: '2.25rem' },
        }}
      >
        Join EncycGames
      </Typography>
      <Typography
        sx={{
          textAlign: 'center',
          color: 'rgba(255,255,255,0.86)',
          mb: 4,
          maxWidth: 680,
          mx: 'auto',
          fontSize: { xs: '1.05rem', md: '1.2rem' },
          lineHeight: 1.55,
        }}
      >
        Players, coaches, academies, sponsors and organizers — pick your path and get started.
      </Typography>

      <Grid container spacing={2.5}>
        {roles.map((role) => (
          <Grid item xs={12} sm={6} md={3} key={role.title}>
            <Box
              sx={{
                p: { xs: 2.5, md: 3 },
                height: '100%',
                borderRadius: 2.5,
                bgcolor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <Box sx={{ color: role.color, mb: 1.25, display: 'flex', '& svg': { fontSize: 32 } }}>{role.icon}</Box>
              <Typography
                sx={{
                  fontFamily: '"Sora", Arial, sans-serif',
                  fontWeight: 800,
                  mb: 0.75,
                  fontSize: { xs: '1.15rem', md: '1.25rem' },
                }}
              >
                {role.title}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mb: 2.5,
                  minHeight: 48,
                  fontSize: { xs: '0.98rem', md: '1.05rem' },
                  lineHeight: 1.55,
                }}
              >
                {role.description}
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate(role.path)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '1rem',
                  py: 1,
                  fontFamily: '"Sora", Arial, sans-serif',
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.35)',
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                Register
              </Button>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" sx={{ mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/login')}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            fontSize: '1.05rem',
            px: 3,
            py: 1.1,
            fontFamily: '"Sora", Arial, sans-serif',
            bgcolor: '#fff',
            color: '#0f172a',
            '&:hover': { bgcolor: '#f1f5f9' },
          }}
        >
          Sign in
        </Button>
        <Button
          variant="text"
          size="large"
          onClick={() => navigate('/team')}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '1.05rem',
            fontFamily: '"Sora", Arial, sans-serif',
            color: '#e2e8f0',
          }}
        >
          Contact our team
        </Button>
      </Stack>
    </Box>
  );
};

export default LandingConvertSection;
